import { Candle, GammaResult, LiquidityZone, ScoringWeights, AppSettings, RegimeResult, RegimeDominant, SMCResult, MarketPhase, TrendResult, TrapSignal } from '../../../../types';
import { detectRegimeModifier } from './regimeModifier';
import { calcVWAP, calcRelVol, calcSessionLevels } from '../../../../utils/calc';

interface ComponentScore {
  scores: { mr: number; bo: number; tr: number; nt: number };
  label: string;
  weight: number;
  explanation: string;
}

// ─── Regime hysteresis (per-symbol) ──────────────────────────────────────────
// Prevents regime from flipping on a single candle.
// New dominant only takes effect after holding for HYSTERESIS_BARS consecutive candles.
const HYSTERESIS_BARS = 2;
interface HysteresisState { candidate: RegimeDominant; count: number; confirmed: RegimeDominant; }
const hysteresisMap = new Map<string, HysteresisState>();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _applyHysteresis(symbolKey: string, rawDominant: RegimeDominant): RegimeDominant {
  const prev = hysteresisMap.get(symbolKey);
  if (!prev) {
    hysteresisMap.set(symbolKey, { candidate: rawDominant, count: 1, confirmed: rawDominant });
    return rawDominant;
  }
  if (rawDominant === prev.confirmed) {
    // Still the same — reset candidate
    hysteresisMap.set(symbolKey, { ...prev, candidate: rawDominant, count: 1 });
    return rawDominant;
  }
  if (rawDominant === prev.candidate) {
    const newCount = prev.count + 1;
    if (newCount >= HYSTERESIS_BARS) {
      // Candidate held long enough — promote to confirmed
      hysteresisMap.set(symbolKey, { candidate: rawDominant, count: 1, confirmed: rawDominant });
      return rawDominant;
    }
    hysteresisMap.set(symbolKey, { ...prev, count: newCount });
    return prev.confirmed; // still returning old confirmed
  }
  // New candidate — start counting
  hysteresisMap.set(symbolKey, { candidate: rawDominant, count: 1, confirmed: prev.confirmed });
  return prev.confirmed;
}

export function scoreRegime(
  candles: Candle[],
  gammaResult: GammaResult | null,
  zones: LiquidityZone[],
  weights: ScoringWeights,
  settings: AppSettings,
  smcResult?: SMCResult | null,
  trendResult?: TrendResult | null,
  /**
   * Phase 2 v1: active trap signals. When passed, detectRegimeModifier
   * can elevate a high-severity directional trap to a regime modifier.
   * Optional and defaults to null so callers that don't pass it get
   * pre-v1 behavior unchanged.
   */
  traps?: TrapSignal[] | null,
): RegimeResult | null {
  if (!candles?.length) return null;

  const cur = candles[candles.length - 1];
  const curPrice = cur.close;
  const vwapArr = calcVWAP(candles);
  const curVWAP = vwapArr[vwapArr.length - 1];
  const levels = calcSessionLevels(candles);
  const rvol = calcRelVol(candles, settings.rvolWindow);
  const vwapDist = (curPrice - curVWAP) / curVWAP * 100;
  const distAbs = Math.abs(vwapDist);

  const dt = new Date(cur.ts);
  const hour = dt.getHours() + dt.getMinutes() / 60;
  const isOpeningRange = hour >= 9.5 && hour <= 9.75;
  const isFirstHour   = hour >= 9.5 && hour <= 10.5;
  const isMidday      = hour >= 11.5 && hour <= 13.5;
  const isPowerHour   = hour >= 15.0 && hour <= 16.0;
  const isClose       = hour >= 15.45;

  const components: Record<string, ComponentScore> = {};

  // ── VWAP ─────────────────────────────────────────────────────────────────
  let vwapScore: ComponentScore['scores'];
  let vwapLabel: string;
  let vwapExpl: string;
  if (distAbs < 0.12) {
    vwapScore = { mr: 35, bo: 12, tr: 12, nt: 22 };
    vwapLabel = `At VWAP (${vwapDist.toFixed(2)}%)`;
    vwapExpl = 'Price at equilibrium — balanced, no strong directional edge from VWAP alone.';
  } else if (distAbs < 0.4) {
    vwapScore = { mr: 22, bo: 24, tr: 22, nt: 12 };
    vwapLabel = `Near VWAP (${vwapDist.toFixed(2)}%)`;
    vwapExpl = 'Price slightly off VWAP — moderate directional edge forming.';
  } else if (distAbs < 0.85) {
    vwapScore = { mr: 12, bo: 34, tr: 34, nt: 8 };
    vwapLabel = `Extended (${vwapDist.toFixed(2)}%)`;
    vwapExpl = 'Price meaningfully extended from VWAP — directional move or mean-reversion candidate.';
  } else {
    vwapScore = { mr: 18, bo: 24, tr: 42, nt: 4 };
    vwapLabel = `Far extended (${vwapDist.toFixed(2)}%)`;
    vwapExpl = `Price is ${distAbs.toFixed(2)}% from VWAP — strongly extended.`;
  }
  components.vwap = { scores: vwapScore, label: vwapLabel, weight: weights.vwap, explanation: vwapExpl };

  // ── Volume ────────────────────────────────────────────────────────────────
  let volScore: ComponentScore['scores'];
  let volLabel: string;
  let volExpl: string;
  if (rvol >= 2.0)      { volScore = { mr: 5, bo: 44, tr: 32, nt: 0 };  volLabel = `High RVOL (${rvol.toFixed(2)}x)`;    volExpl = 'Volume 2× average — strong institutional participation.'; }
  else if (rvol >= 1.35) { volScore = { mr: 10, bo: 34, tr: 26, nt: 8 }; volLabel = `Above avg (${rvol.toFixed(2)}x)`;    volExpl = 'Above-average volume — directional moves more likely to sustain.'; }
  else if (rvol >= 0.75) { volScore = { mr: 22, bo: 18, tr: 16, nt: 24 };volLabel = `Average (${rvol.toFixed(2)}x)`;      volExpl = 'Normal volume — no strong directional edge from volume alone.'; }
  else                   { volScore = { mr: 18, bo: 6, tr: 6, nt: 42 };  volLabel = `Low RVOL (${rvol.toFixed(2)}x)`;     volExpl = 'Below-average volume — low conviction, increased chop risk.'; }
  components.volume = { scores: volScore, label: volLabel, weight: weights.volume, explanation: volExpl };

  // ── Structure (OR) ────────────────────────────────────────────────────────
  const inOR    = curPrice >= levels.orLow && curPrice <= levels.orHigh;
  const aboveOR = curPrice > levels.orHigh;
  // Degrade OR influence after first 2 hours — stale OR location is weak signal later
  const orRelevance = isFirstHour ? 1.0 : isMidday ? 0.4 : 0.6;
  let strScore: ComponentScore['scores'];
  let strLabel: string;
  let strExpl: string;
  if (inOR) {
    strScore = { mr: Math.round(28 * orRelevance), bo: Math.round(10 * orRelevance), tr: Math.round(10 * orRelevance), nt: 30 };
    strLabel = 'Inside OR' + (orRelevance < 1 ? ' (stale)' : '');
    strExpl  = isFirstHour
      ? 'Price inside opening range — no directional commitment, chop likely.'
      : 'Inside OR range late in session — OR location is less relevant, use other signals.';
  } else if (aboveOR) {
    strScore = { mr: 6, bo: Math.round(36 * orRelevance + 12), tr: Math.round(36 * orRelevance + 12), nt: 6 };
    strLabel = 'Above OR High' + (orRelevance < 1 ? ' (degraded)' : '');
    strExpl  = isFirstHour ? 'Above OR high — bullish structure.' : 'Above OR high but OR relevance has faded — use price vs VWAP and swings.';
  } else {
    strScore = { mr: 8, bo: Math.round(32 * orRelevance + 10), tr: Math.round(32 * orRelevance + 10), nt: 8 };
    strLabel = 'Below OR Low' + (orRelevance < 1 ? ' (degraded)' : '');
    strExpl  = isFirstHour ? 'Below OR low — bearish structure.' : 'Below OR low but OR relevance has faded — use price vs VWAP and swings.';
  }
  components.structure = { scores: strScore, label: strLabel, weight: weights.structure, explanation: strExpl };

  // ── Gamma ─────────────────────────────────────────────────────────────────
  let gammaScore: ComponentScore['scores'];
  let gammaLabel: string;
  let gammaExpl: string;
  if (!gammaResult)                              { gammaScore = { mr: 15, bo: 15, tr: 15, nt: 15 }; gammaLabel = 'No options data';      gammaExpl = 'Gamma component excluded — import options OI to enable this factor.'; }
  else if (gammaResult.regime.includes('Long'))  { gammaScore = { mr: 38, bo: 16, tr: 16, nt: 8 };  gammaLabel = `Long Gamma (flip: ${gammaResult.gammaFlip?.toFixed(2)})`; gammaExpl = 'Long gamma — dealers suppress volatility, mean reversion favored.'; }
  else if (gammaResult.regime.includes('Short')) { gammaScore = { mr: 5, bo: 42, tr: 36, nt: 5 };   gammaLabel = `Short Gamma (flip: ${gammaResult.gammaFlip?.toFixed(2)})`; gammaExpl = 'Short gamma — dealers amplify directional moves.'; }
  else                                           { gammaScore = { mr: 18, bo: 22, tr: 22, nt: 18 }; gammaLabel = 'Neutral / Transition';  gammaExpl = 'Near gamma flip — either regime possible; wait for volume confirmation.'; }
  components.gamma = { scores: gammaScore, label: gammaLabel, weight: weights.gamma, explanation: gammaExpl };

  // ── Liquidity ─────────────────────────────────────────────────────────────
  // Only structural/gamma zones strongly drive regime liquidity component
  const inPlayZone = zones.find(z => {
    if (z.sig !== 'High' || z.distPct >= 0.3) return false;
    const cat = (z as LiquidityZone & { category?: string }).category;
    // Exclude pure reference/mean/psychological levels from regime influence
    return cat === 'structural_liquidity' || cat === 'gamma_level' || cat === undefined;
  });
  let liqScore: ComponentScore['scores'];
  let liqLabel: string;
  let liqExpl: string;
  if (inPlayZone) {
    if (inPlayZone.type === 'resistance')      { liqScore = { mr: 24, bo: 28, tr: 20, nt: 8 };  liqLabel = `At resistance: ${inPlayZone.label.substring(0,22)}`; liqExpl = 'At high-significance resistance — reaction imminent.'; }
    else if (inPlayZone.type === 'support')    { liqScore = { mr: 24, bo: 24, tr: 24, nt: 8 };  liqLabel = `At support: ${inPlayZone.label.substring(0,22)}`;    liqExpl = 'At high-significance support — bounce or breakdown decision point.'; }
    else                                        { liqScore = { mr: 22, bo: 22, tr: 22, nt: 12 }; liqLabel = `At key: ${inPlayZone.label.substring(0,22)}`;        liqExpl = 'At high-significance key level — directional resolution expected.'; }
  } else {
    liqScore = { mr: 14, bo: 20, tr: 20, nt: 18 }; liqLabel = 'Between zones'; liqExpl = 'Price between significant zones — less predictable behavior.';
  }
  components.liquidity = { scores: liqScore, label: liqLabel, weight: weights.liquidity, explanation: liqExpl };

  // ── Volatility ────────────────────────────────────────────────────────────
  const last10 = candles.slice(-Math.min(10, candles.length));
  const avgRange = last10.reduce((s, c) => s + (c.high - c.low), 0) / last10.length;
  const rangeRel = (avgRange / curPrice) * 100;
  let volStateScore: ComponentScore['scores'];
  let volStateLabel: string;
  let volStateExpl: string;
  if (rangeRel > 1.2)      { volStateScore = { mr: 5, bo: 34, tr: 42, nt: 5 };  volStateLabel = `High range (${rangeRel.toFixed(2)}%/bar)`;    volStateExpl = 'Wide intraday ranges — elevated volatility, trend/momentum setups have edge.'; }
  else if (rangeRel < 0.25) { volStateScore = { mr: 32, bo: 6, tr: 6, nt: 42 };  volStateLabel = `Low range (${rangeRel.toFixed(2)}%/bar)`;     volStateExpl = 'Compressed volatility — breakout or no-trade conditions dominant.'; }
  else                      { volStateScore = { mr: 20, bo: 20, tr: 20, nt: 16 }; volStateLabel = `Normal range (${rangeRel.toFixed(2)}%/bar)`; volStateExpl = 'Normal volatility — no dominant regime signal from range alone.'; }
  components.volatility = { scores: volStateScore, label: volStateLabel, weight: weights.volatility, explanation: volStateExpl };

  // ── Time of Day ───────────────────────────────────────────────────────────
  let todScore: ComponentScore['scores'];
  let todLabel: string;
  let todExpl: string;
  if (isOpeningRange)    { todScore = { mr: 8, bo: 42, tr: 30, nt: 12 };  todLabel = 'Opening (9:30–9:45)'; todExpl = 'Opening range formation — high volatility, reversals frequent.'; }
  else if (isFirstHour)  { todScore = { mr: 12, bo: 36, tr: 30, nt: 10 }; todLabel = 'First hour';           todExpl = 'First-hour continuation or reversal — primary daily direction often set here.'; }
  else if (isMidday)     { todScore = { mr: 28, bo: 8, tr: 16, nt: 38 };  todLabel = 'Midday (11:30–13:30)'; todExpl = 'Midday chop — institutional flow dries up, noise dominant.'; }
  else if (isPowerHour)  { todScore = { mr: 10, bo: 28, tr: 36, nt: 14 }; todLabel = 'Power hour';           todExpl = 'Power hour — institutional rebalancing drives directional moves.'; }
  else if (isClose)      { todScore = { mr: 15, bo: 20, tr: 30, nt: 22 }; todLabel = 'Close';                todExpl = 'Near close — final positioning, some mean reversion to VWAP.'; }
  else                   { todScore = { mr: 18, bo: 22, tr: 22, nt: 20 }; todLabel = 'Standard session';     todExpl = 'Standard session hours — no strong time-of-day bias.'; }
  components.timeOfDay = { scores: todScore, label: todLabel, weight: weights.timeOfDay, explanation: todExpl };

  // ── SMC component (bonus — adds to scores when SMC signal is clear) ───────
  if (smcResult && smcResult.bias !== 'neutral') {
    const smcBullish = smcResult.bias === 'bullish';
    const smcStrong  = smcResult.biasStrength === 'major';
    const boost      = smcStrong ? 15 : 8;

    // Boost trend if SMC bias aligns
    if (smcBullish && curPrice > curVWAP) {
      components.vwap.scores.tr += boost;
      components.vwap.scores.bo += Math.floor(boost / 2);
    } else if (!smcBullish && curPrice < curVWAP) {
      components.vwap.scores.tr += boost;
      components.vwap.scores.bo += Math.floor(boost / 2);
    }
    // Sweep + reclaim: only meaningful boost if SMC bias is also strong
    if (smcResult.sweepAndReverse && smcStrong) {
      components.liquidity.scores.mr += 12; // was 20 — reduced; requires strong SMC
      components.liquidity.label += ' + sweep+reversal';
    } else if (smcResult.sweepAndReverse) {
      components.liquidity.scores.mr += 5; // minor boost without strong bias
    }
    // CHOCH: small context boost only — not a strong breakout signal on its own
    // Require CHOCH to be accompanied by recent BOS for meaningful boost
    if (smcResult.lastCHOCH && smcResult.lastCHOCH.candleIdx >= candles.length - 8 &&
        smcResult.lastBOS && smcResult.lastBOS.candleIdx >= candles.length - 12) {
      components.structure.scores.bo += 8; // was 15 — reduced, requires BOS accompaniment
      components.structure.label += ' + CHOCH+BOS';
    }
  }

  // ── Aggregate ─────────────────────────────────────────────────────────────
  let totalW = 0;
  const agg = { mr: 0, bo: 0, tr: 0, nt: 0 };
  Object.values(components).forEach(comp => {
    totalW += comp.weight;
    agg.mr += comp.scores.mr * comp.weight;
    agg.bo += comp.scores.bo * comp.weight;
    agg.tr += comp.scores.tr * comp.weight;
    agg.nt += comp.scores.nt * comp.weight;
  });

  const norm = (v: number) => Math.round(v / totalW);
  const scores = { meanReversion: norm(agg.mr), breakout: norm(agg.bo), trend: norm(agg.tr), noTrade: norm(agg.nt) };

  const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]) as [RegimeDominant, number][];
  const dominant = ranked[0][0];

  const LABELS: Record<RegimeDominant, string> = {
    meanReversion: 'Mean Reversion', breakout: 'Breakout', trend: 'Trend Continuation', noTrade: 'No Trade / High Noise'
  };
  const POSTURES: Record<RegimeDominant, string> = {
    meanReversion: curPrice > curVWAP ? 'Fade Extremes — Sell Bounce' : 'Fade Extremes — Buy Dip',
    breakout:      curPrice > curVWAP ? 'Buy Breakout / Retest Long' : 'Sell Breakdown / Bounce Short',
    trend:         curPrice > curVWAP ? 'Buy Pullback to Support' : 'Sell Bounce to Resistance',
    noTrade:       'Stand Aside — Wait for Clarity',
  };

  const domKey = dominant === 'meanReversion' ? 'mr' : dominant === 'breakout' ? 'bo' : dominant === 'trend' ? 'tr' : 'nt';
  Object.entries(components)
    .map(([k, v]) => ({
      key: k, label: v.label, weight: v.weight,
      impact: v.scores[domKey as keyof typeof v.scores] || 0,
      explanation: v.explanation,
    }))
    .sort((a, b) => b.impact * b.weight - a.impact * a.weight)
    .slice(0, 5);

  // ── Phase classification ─────────────────────────────────────────────────
  // Phase is derived from SMC displacement quality, not scorecard scores.
  let phase: MarketPhase = 'RANGE';
  let phaseDriver = 'No significant displacement detected — balanced/range conditions.';

  if (smcResult) {
    const dispStrength  = smcResult.displacementStrength  ?? 0;
    const dispFreshness = smcResult.displacementFreshness ?? 0;
    const dispDir       = smcResult.displacementDirection;
    const eventVol      = smcResult.eventVolumeScore      ?? 0;
    const breaksStr     = smcResult.displacementBreaksStructure;

    // EXPANSION: fresh + strong displacement with structural break or event volume
    const isExpansion = dispStrength >= 0.35 && dispFreshness >= 0.5 &&
      (breaksStr || eventVol >= 0.35 || smcResult.institutionalEventVolume);

    // POST_EXPANSION: displacement was meaningful but is now stale (fading freshness)
    const isPostExpansion = dispStrength >= 0.25 && dispFreshness >= 0.1 && dispFreshness < 0.5;

    if (isExpansion) {
      phase = 'EXPANSION';
      phaseDriver = `${dispDir === 'bullish' ? 'Bullish' : 'Bearish'} expansion — fresh strong displacement`
        + (breaksStr ? ' with structural break' : '')
        + (smcResult.institutionalEventVolume ? ', institutional event volume' : '')
        + '.';

      // Override: during expansion, reduce reversal weight toward displacement direction
      // Do NOT hard-force — just reprioritize the thesis
      const dispBullish = dispDir === 'bullish';
      const overrideBoost = Math.round(dispStrength * 20);
      if (dispBullish) {
        components.vwap.scores.tr += overrideBoost;
        components.vwap.scores.bo += Math.floor(overrideBoost * 0.5);
        components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - overrideBoost);
      } else if (dispDir === 'bearish') {
        components.vwap.scores.tr += overrideBoost;
        components.vwap.scores.bo += Math.floor(overrideBoost * 0.5);
        components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - overrideBoost);
      }
      // Event volume: override low-RVOL dismissal during expansion
      if (smcResult.institutionalEventVolume && rvol < 0.75) {
        components.volume.scores.bo += 15;
        components.volume.scores.tr += 10;
        components.volume.scores.nt  = Math.max(0, components.volume.scores.nt - 20);
        components.volume.label += ' (event vol override)';
      }
      components.vwap.label += ` [${dispDir === 'bullish' ? '↑' : '↓'} EXPANSION]`;

      // ── Trend/phase coupling inside EXPANSION ──────────────────────────────
      // Applied after displacement override so SMC retains priority.
      if (trendResult && trendResult.trendState !== 'compression' && trendResult.trendState !== 'transition') {
        const trendMatchesDisp =
          (dispDir === 'bullish' && trendResult.trendDirection === 'bullish') ||
          (dispDir === 'bearish' && trendResult.trendDirection === 'bearish');
        const trendConflictsDisp =
          (dispDir === 'bullish' && trendResult.trendDirection === 'bearish') ||
          (dispDir === 'bearish' && trendResult.trendDirection === 'bullish');

        if (trendMatchesDisp && trendResult.trendStrength >= 0.35) {
          // Trend aligns — strengthen EXPANSION confidence and continuation scoring
          const alignBonus = Math.round(trendResult.trendStrength * trendResult.alignmentScore * 12);
          components.vwap.scores.tr  = Math.min(components.vwap.scores.tr + alignBonus, 80);
          components.vwap.scores.bo  = Math.min(components.vwap.scores.bo + Math.round(alignBonus * 0.5), 80);
          components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - alignBonus);
          phaseDriver += ` Trend ${trendResult.trendDirection} (${Math.round(trendResult.trendStrength * 100)}%) confirms displacement — expansion strengthened.`;
        } else if (trendConflictsDisp && trendResult.trendStrength >= 0.4) {
          // Trend conflicts — soften EXPANSION toward POST_EXPANSION unless displacement is very strong
          if (dispStrength < 0.65) {
            phase = 'POST_EXPANSION';
            phaseDriver += ` EMA trend (${trendResult.trendDirection}) disagrees with ${dispDir} displacement — reclassified to POST_EXPANSION.`;
          } else {
            // Displacement strong enough to remain EXPANSION but reduce scoring
            const conflictPenalty = Math.round(trendResult.trendStrength * 10);
            components.vwap.scores.tr  = Math.max(0, components.vwap.scores.tr - conflictPenalty);
            components.vwap.scores.bo  = Math.max(0, components.vwap.scores.bo - conflictPenalty);
            components.vwap.scores.mr += Math.round(conflictPenalty * 0.5);
            phaseDriver += ` EMA trend (${trendResult.trendDirection}) conflicts — expansion confidence reduced.`;
          }
        }
      }
    } else if (isPostExpansion) {
      phase = 'POST_EXPANSION';
      phaseDriver = `Post-expansion hold — displacement ${dispFreshness < 0.25 ? 'fading' : 'consolidating'}, `
        + `direction ${dispDir}.`;
      // Modest continuation bias vs reversal during post-expansion
      const postBoost = Math.round(dispStrength * 10);
      components.vwap.scores.tr += postBoost;
      components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - Math.floor(postBoost * 0.5));
    }
  }

  // ── Trend blending (EMA 9/21) ─────────────────────────────────────────────
  // Trend refines directional persistence — it does NOT override SMC/displacement.
  // Applied after phase classification so displacement already has priority.
  if (trendResult) {
    const tr = trendResult;
    const tDir    = tr.trendDirection;
    const tState  = tr.trendState;
    const tStr    = tr.trendStrength;
    const tAlign  = tr.alignmentScore;

    if (tState === 'compression' || tState === 'transition') {
      // EMA compression/transition reduces directional conviction across the board
      const compressionPenalty = Math.round((1 - tStr) * 8);
      components.vwap.scores.bo  = Math.max(0, components.vwap.scores.bo  - compressionPenalty);
      components.vwap.scores.tr  = Math.max(0, components.vwap.scores.tr  - compressionPenalty);
      components.vwap.scores.nt += compressionPenalty;
      components.vwap.label     += ' [EMA compressing]';
      phaseDriver += ` EMA ${tState} reduces conviction.`;

    } else if (tDir !== 'neutral' && tStr >= 0.35) {
      // Meaningful trend — check alignment with current phase + displacement
      const trendBullish = tDir === 'bullish';
      const dispBullish  = (smcResult?.displacementDirection ?? 'none') === 'bullish';
      const dispBearish  = (smcResult?.displacementDirection ?? 'none') === 'bearish';

      // A) Trend aligns with displacement → strengthen EXPANSION, boost continuation
      if ((trendBullish && dispBullish) || (!trendBullish && dispBearish)) {
        const alignBoost = Math.round(tStr * tAlign * 15);
        components.vwap.scores.tr += alignBoost;
        components.vwap.scores.bo += Math.round(alignBoost * 0.5);
        components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - alignBoost);
        phaseDriver += ` EMA trend aligned with ${tDir} displacement (+${alignBoost}).`;

        // If EXPANSION, further strengthen it
        if (phase === 'EXPANSION') {
          const expBoost = Math.round(tStr * 8);
          components.structure.scores.tr += expBoost;
          components.structure.scores.bo += Math.round(expBoost * 0.5);
        }

      // B) Trend disagrees with displacement → reduce EXPANSION confidence,
      //    prefer POST_EXPANSION or RANGE unless displacement is very strong
      } else if ((trendBullish && dispBearish) || (!trendBullish && dispBullish)) {
        const dispStr = smcResult?.displacementStrength ?? 0;
        if (phase === 'EXPANSION' && dispStr < 0.6) {
          phase = 'POST_EXPANSION';
          phaseDriver += ` EMA trend (${tDir}) disagrees — EXPANSION confidence reduced.`;
        }
        const conflictPenalty = Math.round(tStr * 8);
        components.vwap.scores.bo  = Math.max(0, components.vwap.scores.bo  - conflictPenalty);
        components.vwap.scores.tr  = Math.max(0, components.vwap.scores.tr  - conflictPenalty);
        components.vwap.scores.mr += Math.round(conflictPenalty * 0.5);

      // C) Trend direction with no strong displacement → modest directional bias
      } else {
        const quietBoost = Math.round(tStr * tAlign * 8);
        if (trendBullish) {
          components.vwap.scores.tr += quietBoost;
          components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - Math.round(quietBoost * 0.5));
        } else {
          components.vwap.scores.tr += quietBoost;
          components.vwap.scores.mr  = Math.max(0, components.vwap.scores.mr - Math.round(quietBoost * 0.5));
        }
      }
    }
  }

  // ── Re-aggregate after any displacement overrides ─────────────────────────
  let totalW2 = 0;
  const agg2 = { mr: 0, bo: 0, tr: 0, nt: 0 };
  Object.values(components).forEach(comp => {
    totalW2 += comp.weight;
    agg2.mr += comp.scores.mr * comp.weight;
    agg2.bo += comp.scores.bo * comp.weight;
    agg2.tr += comp.scores.tr * comp.weight;
    agg2.nt += comp.scores.nt * comp.weight;
  });

  const norm2 = (v: number) => Math.round(v / totalW2);
  const scores2 = { meanReversion: norm2(agg2.mr), breakout: norm2(agg2.bo), trend: norm2(agg2.tr), noTrade: norm2(agg2.nt) };

  const ranked2   = Object.entries(scores2).sort((a, b) => b[1] - a[1]) as [RegimeDominant, number][];
  const dominant2 = ranked2[0][0];
  const separation2 = ranked2[0][1] - ranked2[1][1];
  const confidence2 = Math.min(Math.max(Math.round(40 + separation2 * 1.8), 35), 92);

  const domKey2 = dominant2 === 'meanReversion' ? 'mr' : dominant2 === 'breakout' ? 'bo' : dominant2 === 'trend' ? 'tr' : 'nt';
  const topDrivers2 = Object.entries(components)
    .map(([k, v]) => ({
      key: k, label: v.label, weight: v.weight,
      impact: v.scores[domKey2 as keyof typeof v.scores] || 0,
      explanation: v.explanation,
    }))
    .sort((a, b) => b.impact * b.weight - a.impact * a.weight)
    .slice(0, 5);

  // Phase 2 v1: detect regime modifier. Pure function over inputs
  // regime already has; new input is traps[] (optional). Modifier is
  // informational only -- macro regime tournament above is unchanged.
  const modifier = detectRegimeModifier(candles, rvol, smcResult ?? null, gammaResult, traps ?? null);

  return {
    scores: scores2, dominant: dominant2, label: LABELS[dominant2],
    confidence: confidence2, posture: POSTURES[dominant2],
    topDrivers: topDrivers2, curVWAP, vwapDist, rvol, levels,
    phase, phaseDriver,
    modifier,
  };
}
